import { useApolloClient, useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Loader, Modal } from '~/components/blocks';
import {
  CompletePractitionerCodeMailPanel,
  CompleteResetPasswordPanel,
  PractitionerResetPasswordPanel,
} from '~/components/partials';
import { Fields } from '~/components/partials/MailSenderForm/types';
import {
  SendSettingsPractitionerCodeMailDocument,
  SettingsPractitionerFragment,
  SettingsPractitionerFragmentDoc,
  UpdateSettingsPractitionerCodeDocument,
} from '~/graphql/generated/graphql';
import { resetPasswordModalState } from '~/state/settings_practitioners/atoms';

export const ResetPasswordModal = () => {
  const apolloClient = useApolloClient();
  const state = useAtomValue(resetPasswordModalState);
  const reset = useResetAtom(resetPasswordModalState);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const practitioner = state.practitionerId
    ? apolloClient.readFragment<SettingsPractitionerFragment>({
        id: `Practitioner:${state.practitionerId}`,
        fragment: SettingsPractitionerFragmentDoc,
      })
    : null;

  const [setPractitionerCode, { data, loading: isResetting, error: resetMutationError }] =
    useMutation(UpdateSettingsPractitionerCodeDocument, {
      // 戻り値のpractitionerCodeをキャッシュしないため
      fetchPolicy: 'no-cache',
    });
  const [
    sendPractitionerCodeMail,
    { data: sendMutationData, loading: isSending, error: sendMutationError },
  ] = useMutation(SendSettingsPractitionerCodeMailDocument);
  const practitionerCode = data?.updatePractitionerCode?.practitionerCode;
  const organization = useMemo(() => {
    if (!data || !data.updatePractitionerCode) return null;
    const _organization = data.updatePractitionerCode.practitioner.organization;
    return {
      slug: _organization.slug,
      name: _organization.name,
    };
  }, [data]);

  const handleClose = useCallback(() => {
    reset();
  }, [reset]);
  const handleReset = useCallback(() => {
    if (state.practitionerId) {
      setPractitionerCode({
        variables: {
          input: {
            practitionerId: state.practitionerId,
          },
        },
      });
    }
  }, [setPractitionerCode, state.practitionerId]);
  const handleSend = useCallback(
    (values: Fields) => {
      if (state.practitionerId && practitionerCode) {
        sendPractitionerCodeMail({
          variables: {
            input: {
              practitionerId: state.practitionerId,
              practitionerCode: practitionerCode,
              email: values.email,
              mailType: 'reset',
            },
          },
        });
      }
    },
    [practitionerCode, sendPractitionerCodeMail, state.practitionerId],
  );

  useEffect(() => {
    if (state.isOpen) {
      setIsCompleted(false);
      setIsSent(false);
    }
  }, [state.isOpen]);

  useEffect(() => {
    if (data) {
      setIsCompleted(true);
    }
  }, [data]);

  useEffect(() => {
    if (resetMutationError) {
      setResetError(resetMutationError.message);
    }
  }, [resetMutationError]);

  useEffect(() => {
    if (sendMutationError) {
      setSendError(sendMutationError.message);
    }
  }, [sendMutationError]);

  useEffect(() => {
    if (sendMutationData) {
      setIsSent(true);
    }
  }, [sendMutationData]);

  return (
    <Modal open={state.isOpen} size="s" onClose={handleClose}>
      {practitioner ? (
        isCompleted ? (
          isSent ? (
            <CompletePractitionerCodeMailPanel />
          ) : organization && practitionerCode ? (
            <CompleteResetPasswordPanel
              error={sendError}
              sending={isSending}
              organization={organization}
              practitioner={{
                familyName: practitioner.familyName,
                givenName: practitioner.givenName,
                username: practitioner.authentication.username,
              }}
              practitionerCode={practitionerCode}
              onSend={handleSend}
            />
          ) : (
            <Loader open inside />
          )
        ) : (
          <PractitionerResetPasswordPanel
            resetting={isResetting}
            error={resetError}
            username={practitioner.authentication.username}
            familyName={practitioner.familyName}
            givenName={practitioner.givenName}
            onReset={handleReset}
          />
        )
      ) : (
        <Modal.Body>
          <Box height="200px">
            <Loader open inside />
          </Box>
        </Modal.Body>
      )}
    </Modal>
  );
};
